import { useScienceTutorSection } from './scienceTutorContext';
import { CheckCircle2, ChevronLeft, ChevronRight, Lightbulb, RotateCcw, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';

import { ElementLegend, FormulaText, MoleculeDiagram } from './MatterClassifierDiagram';
import {
  PURITY_CASES,
  PURITY_LABELS,
  SPECIES,
  caseFacts,
  checkPurity,
  layoutDiagram,
  purityFacts,
  type PurityClass,
} from './matterClassifier';

const PURITY_OPTIONS: PurityClass[] = ['pure', 'mixture'];

const PURITY_HINTS: Record<PurityClass, string> = {
  pure: 'Every particle is the same',
  mixture: 'More than one kind of particle, not joined together',
};

/** Year 7: decide only whether a particle diagram shows a pure substance or a mixture. */
export function PureMixturePanel() {
  const [index, setIndex] = useState(0);
  const [choice, setChoice] = useState<PurityClass | null>(null);
  const [checked, setChecked] = useState(false);
  useScienceTutorSection('PureMixturePanel', { index, choice, checked });
  const item = PURITY_CASES[index];
  const layout = useMemo(() => layoutDiagram(item), [item]);
  const facts = purityFacts(item);
  const elements = caseFacts(item)?.elements ?? [];
  const result = checked && choice ? checkPurity(item, choice) : null;
  const description = item.components
    .map((component) => {
      const species = SPECIES[component.speciesId];
      return species?.lattice ? `a ${species.name}` : `${component.count} × ${species?.name ?? 'unknown particle'}`;
    })
    .join(' and ');

  const go = (next: number) => {
    setIndex(next);
    setChoice(null);
    setChecked(false);
  };

  return (
    <section className="rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8" aria-labelledby="purity-heading">
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Year 7 · Particle diagrams</p>
      <h2 id="purity-heading" className="mt-2 text-2xl font-extrabold text-ink">Pure substance or mixture?</h2>
      <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-slate2">
        A pure substance has only one kind of particle. A mixture has two or more kinds of particle
        that are mixed but not joined. Look at the particles, not at how many atoms each one has.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button type="button" aria-label="Previous sample" disabled={index === 0} onClick={() => go(index - 1)} className="rounded-full border border-hairline p-2 text-ink disabled:opacity-40">
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <div className="min-w-0 flex-1">
          <select
            aria-label="Choose a sample"
            value={item.id}
            onChange={(event) => go(PURITY_CASES.findIndex((candidate) => candidate.id === event.target.value))}
            className="w-full rounded-2xl border border-hairline bg-surface px-3 py-2 text-sm font-extrabold text-ink"
          >
            {PURITY_CASES.map((candidate, position) => (
              <option key={candidate.id} value={candidate.id}>
                {position + 1}. {candidate.title}
              </option>
            ))}
          </select>
        </div>
        <button type="button" aria-label="Next sample" disabled={index >= PURITY_CASES.length - 1} onClick={() => go(index + 1)} className="rounded-full border border-hairline p-2 text-ink disabled:opacity-40">
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0">
          <h3 className="text-lg font-extrabold text-ink">{item.title}</h3>
          <p className="mt-1 text-sm font-medium leading-6 text-slate2">{item.context}</p>
          <div className="mt-3">
            <MoleculeDiagram atoms={layout.atoms} bonds={layout.bonds} label={`Particle diagram: ${description}`} />
          </div>
          <div className="mt-3">
            <ElementLegend elements={elements} />
          </div>
        </div>

        <div className="min-w-0">
          <fieldset>
            <legend className="text-sm font-extrabold text-ink">Your prediction</legend>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {PURITY_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  disabled={checked}
                  aria-pressed={choice === option}
                  onClick={() => setChoice(option)}
                  className={`rounded-2xl border px-4 py-3 text-left text-sm font-extrabold transition-colors disabled:cursor-default ${
                    choice === option ? 'border-brand-sky bg-wash-sky text-ink' : 'border-hairline bg-surface text-slate2 hover:border-brand-sky/40'
                  }`}
                >
                  {PURITY_LABELS[option]}
                </button>
              ))}
            </div>
            <dl className="mt-2 grid gap-1 text-xs font-medium leading-5 text-slate2">
              {PURITY_OPTIONS.map((option) => (
                <div key={option} className="flex gap-1">
                  <dt className="font-extrabold text-ink-soft">{PURITY_LABELS[option]}:</dt>
                  <dd>{PURITY_HINTS[option]}</dd>
                </div>
              ))}
            </dl>
          </fieldset>

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={!choice || checked}
              onClick={() => setChecked(true)}
              className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" /> Check my prediction
            </button>
            <button type="button" onClick={() => go(index)} className="inline-flex items-center gap-2 rounded-full border border-hairline px-5 py-3 text-sm font-extrabold text-ink hover:bg-surface">
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Try again
            </button>
          </div>

          {result && facts ? (
            <div
              role="status"
              aria-label="Purity feedback"
              className={`mt-4 rounded-[1.5rem] border p-5 ${result.correct ? 'border-brand-mint/40 bg-wash-mint' : 'border-brand-coral/35 bg-wash-coral'}`}
            >
              <div className="flex items-start gap-3">
                {result.correct ? (
                  <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-brand-mint" aria-hidden="true" />
                ) : (
                  <Lightbulb className="mt-0.5 h-6 w-6 shrink-0 text-brand-coral" aria-hidden="true" />
                )}
                <div>
                  <p className="font-extrabold text-ink">
                    {result.correct
                      ? `Correct — ${PURITY_LABELS[result.expected].toLowerCase()}`
                      : `Not quite — this is ${result.expected === 'pure' ? 'a pure substance' : 'a mixture'}`}
                  </p>
                  <p className="mt-2 font-medium leading-7 text-slate2">{result.feedback}</p>
                </div>
              </div>
              <p className="mt-4 rounded-2xl bg-white/75 px-4 py-2 text-sm font-bold text-ink-soft">
                Model count: <strong className="text-ink">{facts.particleKinds}</strong>{' '}
                {facts.particleKinds === 1 ? 'kind of particle' : 'kinds of particle'} —{' '}
                {facts.particleFormulas.map((formula, position) => (
                  <span key={formula}>
                    {position > 0 ? ', ' : ''}
                    <FormulaText formula={formula} />
                  </span>
                ))}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
